import Dexie, { type Table } from 'dexie';

export interface DBProduct {
  id: number;
  storeId: string;
  name: string;
  slug: string;
  status: string;
  description: string;
  short_description: string;
  price: string;
  regular_price: string;
  sale_price: string;
  stock_status: string;
  stock_quantity: number | null;
  categories: any[];
  images: any[];
  attributes: any[];
  variations: number[];
  brand_ids: number[];
  date_modified: string;
  date_modified_gmt: string;
  meta_data: any[];
  _embedded?: any;
}

export interface DBCategory {
  id: number;
  storeId: string;
  name: string;
  slug: string;
  parent: number;
  count: number;
  image?: any;
}

export interface DBBrand {
  id: number;
  storeId: string;
  name: string;
  slug: string;
  description: string;
  count: number;
  logo_url?: string;
  image?: any;
}

export interface DBCustomer {
  id: number;
  storeId: string;
  email: string;
  first_name: string;
  last_name: string;
  username: string;
  role: string;
  avatar_url?: string;
}

export interface DBOrder {
  id: number;
  storeId: string;
  number: string;
  status: string;
  total: string;
  currency: string;
  date_created: string;
  date_created_gmt: string;
  billing: any;
  shipping: any;
  line_items: any[];
  customer_id: number;
}

export interface DBMedia {
  id: number;
  storeId: string;
  source_url: string;
  title: { rendered: string };
  alt_text?: string;
  media_details?: any;
}

export interface DBAttribute {
  id: number;
  storeId: string;
  name: string;
  slug: string;
  type: string;
  order_by: string;
  has_archives: boolean;
}

export interface DBAttributeTerm {
  id: number;
  storeId: string;
  attributeId: number;
  name: string;
  slug: string;
  count: number;
}

export interface DBVariation {
  id: number;
  storeId: string;
  productId: number;
  sku: string;
  price: string;
  regular_price: string;
  sale_price: string;
  stock_status: string;
  stock_quantity: number | null;
  attributes: any[];
  image?: any;
}

export interface SyncMetadata {
  key: string;
  storeId: string;
  timestamp: number;
}

export class SwiftWooDB extends Dexie {
  products!: Table<DBProduct>;
  categories!: Table<DBCategory>;
  brands!: Table<DBBrand>;
  customers!: Table<DBCustomer>;
  orders!: Table<DBOrder>;
  gallery!: Table<DBMedia>;
  attributes!: Table<DBAttribute>;
  attributeTerms!: Table<DBAttributeTerm>;
  variations!: Table<DBVariation>;
  syncMetadata!: Table<SyncMetadata>;

  constructor() {
    // Renamed database to avoid "Not yet support for changing primary key" error
    super('SwiftWooManager_DB_v1');
    
    this.version(1).stores({
      products: '[id+storeId], name, slug, status, stock_status, date_modified_gmt, storeId',
      categories: '[id+storeId], name, slug, parent, storeId',
      brands: '[id+storeId], name, slug, storeId',
      customers: '[id+storeId], email, username, storeId',
      orders: '[id+storeId], number, status, date_created_gmt, storeId',
      gallery: '[id+storeId], source_url, storeId',
      attributes: '[id+storeId], name, slug, storeId',
      attributeTerms: '[id+storeId], attributeId, name, slug, storeId',
      variations: '[id+storeId], [productId+storeId], sku, storeId',
      syncMetadata: '[key+storeId], timestamp, storeId'
    });

    this.version(2).stores({
      attributeTerms: '[id+storeId], [storeId+attributeId], attributeId, name, slug, storeId'
    });
  }

  async clearStoreData(storeId: string) {
    const tables = [
      this.products, 
      this.categories, 
      this.brands, 
      this.customers, 
      this.orders, 
      this.gallery, 
      this.attributes, 
      this.attributeTerms, 
      this.variations, 
      this.syncMetadata
    ];
    
    const deletePromises = tables.map(table => 
      table.where('storeId').equals(storeId).delete()
    );
    
    await Promise.all(deletePromises);
  }
}

export const db = new SwiftWooDB();

// Helper to get active store ID
export const getActiveStoreId = () => {
  if (typeof window === 'undefined') return 'default';
  return localStorage.getItem('wc_active_store_id') || 'default';
};
